import type { ChangeEvent, ClipboardEvent } from 'react';
import { useId } from 'react';

import { FormField } from './FormField.js';

/**
 * Recovery-code input. Codes are XXXX-XXXX-XXXX-XXXX (16 chars
 * across 4 groups of 4) using a Crockford-style alphabet (no I, O,
 * 0, 1). This component formats the live value: as the user types,
 * dashes are inserted after every 4 characters, lowercase is
 * upcased, and the ambiguous characters are dropped silently.
 *
 * Paste support: pasting `abcd-efgh-jklm-npqr`, `abcdefghjklmnpqr`,
 * or any mixed-case variant all reformat to the canonical
 * `ABCD-EFGH-JKLM-NPQR`. Excess characters past 16 are trimmed.
 *
 * The component is controlled — parent owns `value` and `onChange`.
 * The displayed string IS the value (with dashes); the parent posts
 * it as-is to /auth/login/verify-recovery-code where the server is
 * tolerant of separators.
 *
 * Accessibility:
 *  - autoComplete is not "one-time-code" here (those are usually
 *    6-digit numeric, which mobile keyboards interpret as numeric
 *    pads). We mark it `autoComplete="off"` and rely on the helper
 *    text to set expectations.
 */
const ALPHA = /[A-HJ-NP-Z2-9]/g;

function formatGroups(raw: string): string {
  const upper = raw.toUpperCase();
  const filtered = (upper.match(ALPHA) ?? []).join('').slice(0, 16);
  // Split into 4-char groups.
  const groups: string[] = [];
  for (let i = 0; i < filtered.length; i += 4) {
    groups.push(filtered.slice(i, i + 4));
  }
  return groups.join('-');
}

type Props = {
  label: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  helper?: string;
  disabled?: boolean;
};

export function RecoveryCodeInput({ label, value, onChange, error, helper, disabled }: Props) {
  const reactId = useId();
  const id = `rc-${reactId}`;

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(formatGroups(e.target.value));
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    e.preventDefault();
    onChange(formatGroups(text));
  }

  return (
    <FormField
      label={label}
      helper={helper ?? 'Format : XXXX-XXXX-XXXX-XXXX (16 caractères)'}
      error={error}
      renderInput={({ id: fid, describedBy, invalid }) => (
        <input
          id={fid || id}
          type="text"
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          disabled={disabled}
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          maxLength={19}
          className={
            'input-base font-tabular tracking-widest text-center ' +
            (invalid ? 'border-semantic-error focus:border-semantic-error ' : '')
          }
        />
      )}
    />
  );
}
